import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { BankAuthorization, Button, ButtonLink, EmptyState, FlowLayout } from '@/ds'
import type { BankAuthState } from '@/ds'
import { dayMonthLong, formatMoney } from '@/lib/format'
import { whatsappHref } from '@/lib/whatsapp'
import { useAppNavigate } from '@/lib/query-params'
import { useAsync } from '@/lib/useAsync'
import { extrasService } from '@/services/extras'
import { fianzaService } from '@/services/fianza'
import { pagosService, readPendingPayment, writePendingPayment } from '@/services/pagos'
import type { PaymentOutcome } from '@/services/pagos'
import { reservaService } from '@/services/reserva'
import { useApiLang } from '../Checkin/useSave'
import { enablePartial, PAY_ROUTES, usePagosStay } from './shared'
import { RETURN_PARAM, useChangeExit } from '../Reserva/shared'
import type { AuthorizeState } from './usePaymentStart'

/**
 * Autorización del pago en el banco (inventario §4.1 y D34). El iframe es el de la app actual (sin estilos ni
 * postMessage); alrededor, los estados del sistema. El pago en curso vive en la pestaña: si la página se recarga
 * o el banco devuelve a la app, se retoma la espera de E4 con el mismo `temporalPaidId` sin cobrar otra vez.
 * Tras el tiempo límite no se da el pago por fallido: se ofrece comprobarlo de nuevo.
 *
 * Bloque 5: también autoriza el cobro de un cambio de reserva ya aplicado con E12 (`kind: 'change'`, D45). La espera
 * es la misma (E4, vía `reserva.waitForChange`). Autorizado → confirmación del cambio; rechazado → "sin pago", que
 * intenta volver a las fechas originales; sin respuesta → no se revierte (el cobro puede completarse) y Reserva
 * muestra el cambio pendiente de pago hasta que E2 no tenga nada pendiente.
 */
export function Autorizar() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const apiLang = useApiLang()
  const stay = usePagosStay()
  const { go, search } = useAppNavigate()
  const exit = useChangeExit()
  const hasOrigin = new URLSearchParams(search).has(RETURN_PARAM)
  const routeState = useLocation().state as AuthorizeState | null
  const [pending] = useState(() => readPendingPayment(stay.guestCode))
  const completed = routeState?.completed ?? null
  const [phase, setPhase] = useState<BankAuthState | 'none'>(completed ? 'authorized' : pending ? 'waiting' : 'none')
  const [checking, setChecking] = useState(false)
  const controller = useRef<AbortController | null>(null)

  const meta = completed ?? (pending ? { kind: pending.kind, amount: pending.amount, currency: pending.currency, returnTo: pending.returnTo, last4: routeState?.last4 ?? null } : null)
  const isDeposit = meta?.kind === 'deposit'
  const changeId = pending?.kind === 'change' ? (pending.changeId ?? null) : null
  const isChange = meta?.kind === 'change'
  const checkoutId = pending?.kind === 'extras' ? (pending.checkoutId ?? null) : null
  const isExtras = meta?.kind === 'extras'
  const wait = (signal: AbortSignal | undefined, timeoutMs?: number): Promise<PaymentOutcome> => {
    const options = { ...(signal ? { signal } : {}), ...(timeoutMs !== undefined ? { timeoutMs } : {}) }
    if (!pending) return Promise.resolve('rejected')
    if (changeId) return reservaService.waitForChange(stay, changeId, options)
    if (checkoutId) return extrasService.waitForCheckout(stay, checkoutId, options)
    return pagosService.waitForResult(stay, pending.temporalPaidId, options)
  }
  const amountText = meta ? formatMoney({ amount: meta.amount, currency: meta.currency }, lang) : ''

  const returnTo = meta?.returnTo ?? PAY_ROUTES.payments
  const retryTo = pending?.retryTo ?? returnTo
  const settle = (outcome: PaymentOutcome) => {
    if (outcome === 'completed') {
      writePendingPayment(null)
      setPhase('authorized')
    } else if (outcome === 'rejected') {
      writePendingPayment(null)
      if (isChange) {
        go(retryTo, { replace: true, state: { changeId, cause: 'rejected' } })
        return
      }
      // Un carrito de extras rechazado no contrata nada: se vuelve a intentar desde el carrito, sin pago parcial.
      if (!isExtras) enablePartial(stay.guestCode)
      setPhase('rejected')
    } else setPhase('timeout')
  }

  useEffect(() => {
    if (phase !== 'waiting' || !pending) return
    const ctrl = new AbortController()
    controller.current = ctrl
    wait(ctrl.signal)
      .then(settle)
      .catch(() => undefined)
    return () => ctrl.abort()
    // settle solo escribe estado; la espera depende del pago y de la fase.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, pending, stay])

  const checkAgain = () => {
    if (!pending) return
    setChecking(true)
    wait(undefined, 0)
      .then(settle)
      .catch(() => setPhase('timeout'))
      .finally(() => setChecking(false))
  }

  const [deposit] = useAsync(`autorizar-fianza:${stay.guestCode}:${phase}`, (signal) => (isDeposit && phase === 'authorized' ? fianzaService.getDeposit(stay, apiLang, signal) : Promise.resolve(null)))
  const holdUntil = deposit.kind === 'ready' ? (deposit.value?.holdUntil ?? null) : null

  const leave = () => {
    controller.current?.abort()
    // Un cambio sin respuesta del banco no se revierte: se vuelve a Reserva, que lo muestra pendiente de pago.
    if (isChange && phase !== 'authorized') go(PAY_ROUTES.booking, { replace: true, query: { volver: null } })
    // Pago o fianza autorizados con origen (`volver`, B4): se vuelve a donde se entró, como al terminar un cambio.
    else if (phase === 'authorized' && hasOrigin && !isChange && !isExtras) exit.exit({ replace: true })
    else go(phase === 'authorized' ? returnTo : retryTo, { replace: true })
  }

  // El cambio ya está aplicado: su confirmación es el paso 4 del flujo. Los extras cobrados: "Extras confirmados".
  useEffect(() => {
    if ((isChange || isExtras) && phase === 'authorized') go(returnTo, { replace: true, state: { changeId, last4: routeState?.last4 ?? null } })
    // go cambia en cada render; solo importa el momento en que se autoriza.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChange, isExtras, phase])

  const contact = stay.contact.whatsapp ? (
    <ButtonLink variant="secondary" icon="message-circle" href={whatsappHref(stay.contact.whatsapp, stay.reference)} target="_blank" rel="noreferrer">
      {t('v2.ds.contact.whatsapp')}
    </ButtonLink>
  ) : null

  return (
    <FlowLayout
      progress={
        isDeposit
          ? { step: 3, total: 3, backLabel: t('v2.fianza.reservar.atras'), onBack: leave, saved: false }
          : isChange
            ? { step: 3, total: 4, backLabel: t('v2.comun.atras'), onBack: leave, saved: false }
            : isExtras
              ? { step: 2, total: 2, backLabel: t('v2.extras.confirmar.volver'), onBack: leave, saved: false }
            : { step: 2, total: 2, backLabel: t('v2.pagos.autorizar.atras'), onBack: leave, saved: false }
      }
      title={isDeposit ? t('v2.fianza.reservar.titulo') : isChange ? t('v2.reserva.pagar.titulo') : isExtras ? t('v2.extras.confirmar.titulo') : t('v2.pagos.autorizar.titulo')}
    >
      {phase === 'none' || !meta ? (
        <EmptyState icon="credit-card" title={t('v2.pagos.autorizar.sinPago')} headingLevel={2} actionLabel={t('v2.pagos.autorizar.volverPagos')} onAction={() => go(PAY_ROUTES.payments, { replace: true })}>
          {t('v2.pagos.autorizar.sinPagoTexto')}
        </EmptyState>
      ) : (
        <BankAuthorization
          state={phase}
          src={pending?.iframeUrl ?? null}
          amount={amountText}
          checking={checking}
          onStopWaiting={() => {
            controller.current?.abort()
            setPhase('timeout')
          }}
          onCheckAgain={checkAgain}
          onRetry={leave}
          onContinue={leave}
          {...(isChange ? { rejectedText: t('v2.reserva.pagar.rechazado') } : {})}
          {...(isExtras ? { rejectedText: t('v2.extras.autorizar.rechazado') } : {})}
          {...(isDeposit
            ? {
                authorizedTitle: t('v2.fianza.reservar.hecho.titulo'),
                authorizedText: `${meta.last4 ? t('v2.fianza.reservar.hecho.tarjeta', { last4: meta.last4 }) : t('v2.fianza.reservar.hecho.tuTarjeta')} ${holdUntil ? t('v2.fianza.reservar.hecho.libera', { date: dayMonthLong(holdUntil, lang) }) : t('v2.fianza.reservar.hecho.liberaSinFecha')}`,
              }
            : {})}
        >
          {phase === 'timeout' && (
            <>
              {isChange && <p className="pagos__note">{t('v2.reserva.pagar.sinRespuesta')}</p>}
              <Button variant="secondary" onClick={leave}>
                {t('v2.pagos.autorizar.volver')}
              </Button>
              {contact}
            </>
          )}
          {phase === 'rejected' && contact}
        </BankAuthorization>
      )}
    </FlowLayout>
  )
}
